/**
 * Searchable dropdown for attaching file-manager documents to a task or a
 * checklist step. Workspace (this incident) and organization lists are toggled
 * at the top rather than stacked, so the menu stays one list at a time.
 *
 * Deliberately not `IapFileAttachmentPicker`: that one filters by
 * `canAppendToIap` and caps on how many rows the printed cover has room for. A
 * ticket attachment just points at evidence, so any file type is fair game.
 */

import React, { useId, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Eye, Folder, Paperclip, Upload } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useOptionalIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import {
  downloadBlobAsFile,
  fetchDownloadSignedUrl,
} from 'components/FileManagement/hooks/useNotebookQueries';
import { useGetAllFiles } from 'components/FileManagement/hooks/useGetAllFiles';
import type {
  FileOrFolder,
  NotebookFile,
} from 'components/FileManagement/hooks/useGetAllFiles';
import { useUploadFiles } from 'components/FileManagement/hooks/fileMutations';
import { selectGroupGuid, useAppSelector } from 'slices/commonSelectors';
import { WORKSPACE_TYPES } from '../../../types/workspace';
import { isPreviewableDocument, previewMimeType } from './documentPreview';

export type WorkspaceFileRef = {
  fileId: string;
  label: string;
  /** Folders reuse `fileId` as the folder id. Defaults to a file. */
  kind?: 'file' | 'folder';
};

type FileScope = 'workspace' | 'organization';

type PickableFile = {
  id: string;
  name: string;
  kind: 'file' | 'folder';
  size: string;
  fileUrl?: string | null;
  mimetype?: string | null;
};

type WorkspaceFilePickerProps = {
  /** Absent for an org-scoped action, which can still pick from the org. */
  incidentId?: string | null;
  selected: WorkspaceFileRef[];
  onChange: (attachments: WorkspaceFileRef[]) => void;
  /** Checklist items cap at 10; task attachments leave this unset. */
  maxFiles?: number;
  /** Task attachments can point at a folder as well as a file. */
  allowFolders?: boolean;
};

function toPickableFiles(
  dataset: FileOrFolder[] | undefined,
  allowFolders: boolean
): PickableFile[] {
  return (dataset ?? [])
    .filter(
      (item) => item.type === 'file' || (allowFolders && item.type === 'folder')
    )
    .map((item) =>
      item.type === 'folder'
        ? {
            id: String(item.id),
            name: item.folder_name,
            kind: 'folder' as const,
            size: '',
            fileUrl: null,
            mimetype: null,
          }
        : {
            id: String(item.id),
            name: item.file_name,
            kind: 'file' as const,
            size: item.size ?? '',
            fileUrl: item.fileUrl,
            mimetype: item.mimetype,
          }
    )
    .sort((a, b) =>
      a.name.localeCompare(b.name, undefined, {
        numeric: true,
        sensitivity: 'base',
      })
    );
}

/** Incident + org files, so an already-attached id can still be previewed. */
export function useWorkspaceFileMap(incidentId?: string | null) {
  const groupGuid = useAppSelector(selectGroupGuid);
  const { data: incidentFiles } = useGetAllFiles(incidentId ?? undefined);
  const { data: orgFiles } = useGetAllFiles(groupGuid, WORKSPACE_TYPES.ORG);
  return useMemo(() => {
    const map = new Map<string, NotebookFile>();
    for (const item of [
      ...(incidentFiles?.dataset ?? []),
      ...(orgFiles?.dataset ?? []),
    ]) {
      if (item.type === 'file') map.set(String(item.id), item);
    }
    return map;
  }, [incidentFiles, orgFiles]);
}

const WorkspaceFilePicker: React.FC<WorkspaceFilePickerProps> = ({
  incidentId,
  selected,
  onChange,
  maxFiles,
  allowFolders = false,
}) => {
  const preview = useOptionalIcsFormPdfPreview();
  const groupGuid = useAppSelector(selectGroupGuid);
  const uploadFiles = useUploadFiles();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const rowId = useId();
  const hasWorkspace = Boolean(incidentId);
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [scope, setScope] = useState<FileScope>(
    hasWorkspace ? 'workspace' : 'organization'
  );
  const [previewingId, setPreviewingId] = useState<string | null>(null);

  const { data: workspaceFiles } = useGetAllFiles(
    open && incidentId ? incidentId : undefined
  );
  const { data: orgFiles } = useGetAllFiles(
    open ? groupGuid : undefined,
    WORKSPACE_TYPES.ORG
  );

  const visibleFiles = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    const source =
      scope === 'workspace'
        ? toPickableFiles(workspaceFiles?.dataset, allowFolders)
        : toPickableFiles(orgFiles?.dataset, allowFolders);
    if (!needle) return source;
    return source.filter((file) => file.name.toLowerCase().includes(needle));
  }, [allowFolders, orgFiles, scope, searchTerm, workspaceFiles]);

  const selectedKey = (fileId: string, kind: 'file' | 'folder' = 'file') =>
    `${kind}:${fileId}`;
  const selectedIds = selected.map((file) =>
    selectedKey(file.fileId, file.kind ?? 'file')
  );
  const atLimit = maxFiles != null && selected.length >= maxFiles;

  const toggle = (file: PickableFile) => {
    const key = selectedKey(file.id, file.kind);
    onChange(
      selectedIds.includes(key)
        ? selected.filter(
            (entry) => selectedKey(entry.fileId, entry.kind ?? 'file') !== key
          )
        : [...selected, { fileId: file.id, label: file.name, kind: file.kind }]
    );
  };

  const openPreview = async (file: PickableFile) => {
    if (!preview || !file.fileUrl) return;
    setPreviewingId(file.id);
    try {
      const signed = await fetchDownloadSignedUrl(file.fileUrl);
      preview.previewFromSignedUrl({
        fileUrl: signed,
        fileName: file.name,
        mimeType: previewMimeType(file.name, file.mimetype),
        onDownload: () => downloadBlobAsFile(file.fileUrl as string, file.name),
      });
    } catch {
      toast.error('Unable to open preview.');
    } finally {
      setPreviewingId(null);
    }
  };

  /**
   * An upload lands in the workspace the action belongs to and is attached
   * straight away, which is the whole point of offering it here rather than
   * sending someone to File Management and back. `ragIndex: false` keeps a
   * photo of a signed form out of the assistant's corpus.
   */
  const handleUpload = async (files: File[]) => {
    if (files.length === 0) return;
    const room = maxFiles == null ? files.length : maxFiles - selected.length;
    if (room <= 0) {
      toast.error(
        maxFiles != null
          ? `At most ${maxFiles} files can be attached here.`
          : 'No more files can be attached.'
      );
      return;
    }

    try {
      const uploaded = await uploadFiles.mutateAsync({
        files: files.slice(0, room),
        ...(incidentId
          ? { workspaceId: incidentId }
          : { workspaceId: groupGuid, scopeType: WORKSPACE_TYPES.ORG }),
        ragIndex: false,
      });

      const rows: WorkspaceFileRef[] = (uploaded ?? [])
        .filter((file: { id?: string }) => !!file?.id)
        .map((file: { id?: string; file_name?: string }, index: number) => ({
          fileId: file.id as string,
          label: file.file_name || files[index]?.name || '',
        }));

      if (rows.length === 0) {
        toast.error('The file was uploaded but could not be attached.');
        return;
      }
      onChange([...selected, ...rows]);
    } catch {
      // useUploadFiles already surfaces the failure.
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) setSearchTerm('');
      }}
    >
      <PopoverTrigger
        render={<Button type="button" variant="outline" size="sm" />}
      >
        <Paperclip />
        {allowFolders ? 'Add files or folders' : 'Add files'}
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="dark tw:w-80 tw:p-2"
        positionerClassName="tw:z-[1100]"
      >
        <div className="tw:flex tw:flex-col tw:gap-2">
          {hasWorkspace ? (
            <ToggleGroup
              variant="outline"
              size="sm"
              spacing={0}
              multiple={false}
              value={[scope]}
              onValueChange={(next) => {
                const value = Array.isArray(next) ? next[0] : next;
                if (value === 'workspace' || value === 'organization') {
                  setScope(value);
                }
              }}
              aria-label="File source"
              className="tw:w-full tw:[&_[data-slot=toggle-group-item]]:flex-1"
            >
              <ToggleGroupItem value="workspace">Workspace</ToggleGroupItem>
              <ToggleGroupItem value="organization">
                Organization
              </ToggleGroupItem>
            </ToggleGroup>
          ) : null}

          <div className="tw:flex tw:items-center tw:gap-2">
            <Input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder={
                allowFolders ? 'Search files and folders' : 'Search files'
              }
              aria-label={
                allowFolders ? 'Search files and folders' : 'Search files'
              }
            />
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="tw:hidden"
              data-testid="workspace-file-input"
              onChange={(event) => {
                const files = Array.from(event.target.files ?? []);
                event.target.value = '';
                void handleUpload(files);
              }}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={atLimit || uploadFiles.isPending}
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload />
              {uploadFiles.isPending ? 'Uploading…' : 'Upload'}
            </Button>
          </div>

          <div className="tw:max-h-72 tw:overflow-y-auto tw:rounded-md tw:border tw:border-border">
            {visibleFiles.length === 0 ? (
              <p className="tw:p-3 tw:text-sm tw:text-muted-foreground">
                {allowFolders
                  ? 'No files or folders yet. Upload a file to attach it.'
                  : 'No files yet. Upload one to attach it.'}
              </p>
            ) : (
              <ul className="tw:m-0 tw:list-none tw:p-0">
                {visibleFiles.map((file) => {
                  const checked = selectedIds.includes(
                    selectedKey(file.id, file.kind)
                  );
                  const canPreview = Boolean(
                    file.kind === 'file' &&
                      preview &&
                      file.fileUrl &&
                      isPreviewableDocument(file.name, file.mimetype)
                  );
                  return (
                    <li
                      key={file.id}
                      className="tw:flex tw:items-center tw:gap-2 tw:border-t tw:border-border tw:px-2 tw:py-2 tw:first:border-t-0"
                    >
                      <Checkbox
                        id={`${rowId}-${file.id}`}
                        checked={checked}
                        disabled={atLimit && !checked}
                        onCheckedChange={() => toggle(file)}
                      />
                      <label
                        htmlFor={`${rowId}-${file.id}`}
                        className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col"
                      >
                        <span className="tw:flex tw:min-w-0 tw:items-center tw:gap-1 tw:truncate tw:text-sm">
                          {file.kind === 'folder' ? (
                            <Folder className="tw:size-3.5 tw:shrink-0 tw:text-muted-foreground" />
                          ) : null}
                          <span className="tw:truncate">{file.name}</span>
                        </span>
                        {file.size ? (
                          <span className="tw:text-xs tw:text-muted-foreground">
                            {file.size}
                          </span>
                        ) : null}
                      </label>
                      {canPreview ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          aria-label={`Preview ${file.name}`}
                          disabled={previewingId === file.id}
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            void openPreview(file);
                          }}
                        >
                          <Eye />
                        </Button>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
            {maxFiles != null
              ? `${selected.length} of ${maxFiles} attached`
              : `${selected.length} attached`}
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default WorkspaceFilePicker;
